import { useState } from 'react'

function EyeIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 13.5 2.5 2.5"/><path d="M6.5 6.6A2 2 0 0 0 9.4 9.5"/><path d="M4.2 4.3C2.8 5.2 1.7 6.5 1 8c1.3 2.8 4.2 5 7 5 1.2 0 2.4-.4 3.4-1"/><path d="M10.6 10.7C12.2 9.8 13.4 8.5 15 8c-1.3-2.8-4.2-5-7-5-.7 0-1.4.1-2 .4"/>
    </svg>
  )
}
import { Link, Navigate, useNavigate } from 'react-router-dom'
import BackgroundFX from '../components/backgroundFX'
import FormField from '../components/formField'
import Logo from '../components/logo'
import ThemeToggle from '../components/themeToggle'
import HelpButton from '../components/helpButton'

import { STRINGS } from '../constants/strings'
import { useAuth } from '../hooks'

function AuroraBackground() { return null }

const STAGGER = i => ({ animation: 'fade-up 0.5s ease both', animationDelay: `${0.1 + i * 0.08}s` })

export default function LoginPage() {
  const { login, isAuthenticated, user } = useAuth()
  const navigate  = useNavigate()
  const [form,     setForm]    = useState({ email: '', password: '' })
  const [error,    setError]   = useState('')
  const [loading,  setLoading] = useState(false)
  const [showPass, setShowPass] = useState(false)

  if (isAuthenticated) {
    if (user?.role === 'superadmin') return <Navigate to="/superadmin" replace />
    if (user?.role === 'organisation') return <Navigate to="/admin" replace />
    return <Navigate to="/" replace />
  }

  const set = k => v => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const u = await login(form)
      if (u?.role === 'superadmin') navigate('/superadmin')
      else if (u?.role === 'organisation') navigate('/admin')
      else navigate('/')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <AuroraBackground />
      <BackgroundFX />
      <ThemeToggle fixed />
      <HelpButton fixed />

      <div className="relative z-10 w-full sm:max-w-[60%]">
        <div>
          <div className="card p-10 sm:p-12 sm:px-20">

            <div style={STAGGER(0)} className="text-center mb-6">
              <Logo size="auth" />
            </div>

            <div style={STAGGER(0)} className="text-center">
              <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text-1)' }}>
                {STRINGS.auth.loginTitle}
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
                {STRINGS.auth.loginSubtitle}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div style={STAGGER(1)}>
                <FormField
                  label={STRINGS.auth.emailLabel}
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder={STRINGS.auth.emailPlaceholder}
                  autoComplete="email"
                  maxLength={100}
                />
              </div>
              <div style={STAGGER(2)} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>{STRINGS.auth.passwordLabel}</label>
                  <Link to="/forgotPassword" className="text-xs" style={{ color: 'var(--text-3)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-2)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}>
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => set('password')(e.target.value)}
                    placeholder={STRINGS.auth.passwordPlaceholder}
                    autoComplete="current-password"
                    maxLength={20}
                    required
                    className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-shadow ring-1"
                    style={{ background: 'var(--surface)', color: 'var(--text-1)' }}
                    onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)'}
                    onBlur={e  => e.currentTarget.style.boxShadow = '0 0 0 1px var(--border)'}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPass(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center transition-colors"
                    style={{ color: showPass ? 'var(--brand)' : 'var(--text-3)' }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--brand)' }}
                    onMouseLeave={e => { e.currentTarget.style.color = showPass ? 'var(--brand)' : 'var(--text-3)' }}
                  >
                    <EyeIcon open={showPass} />
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 animate-fade-up" style={{ color: 'var(--text-1)' }}>
                  {error}
                </p>
              )}

              <div style={STAGGER(3)}>
                <button type="submit" disabled={loading}
                  className="btn-primary w-full justify-center mt-2 disabled:opacity-60">
                  {loading
                    ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />{STRINGS.auth.loginLoading}</>
                    : STRINGS.auth.loginCta}
                </button>
              </div>
            </form>

            <p className="text-xs text-center mt-6" style={{ color: 'var(--text-3)', ...STAGGER(4) }}>
              {STRINGS.auth.loginSwitch}{' '}
              <Link to="/register" className="font-medium hover:underline" style={{ color: 'var(--brand)' }}>
                {STRINGS.auth.loginSwitchLink}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes drift1 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(60px,-50px) scale(1.1); }
          66%      { transform: translate(-30px,30px) scale(0.9); }
        }
        @keyframes drift2 {
          0%,100% { transform: translate(0,0) scale(1); }
          33%      { transform: translate(-50px,40px) scale(1.12); }
          66%      { transform: translate(35px,-25px) scale(0.93); }
        }
      `}</style>
    </div>
  )
}
